/**
 * Minimal typed event bus. The engine publishes; render, vfx, audio and ui
 * subscribe. Nothing downstream of the engine is allowed to mutate game state,
 * which keeps the rules portable to a server later.
 */

import type { GameEvent } from '@/game/types';

type Handler<E extends GameEvent = GameEvent> = (event: E) => void;

export type GameEventType = GameEvent['type'];

export class EventBus {
  #handlers = new Map<string, Set<Handler>>();
  #any = new Set<Handler>();

  on<T extends GameEventType>(
    type: T,
    handler: (event: Extract<GameEvent, { type: T }>) => void,
  ): () => void {
    let set = this.#handlers.get(type);
    if (!set) {
      set = new Set();
      this.#handlers.set(type, set);
    }
    set.add(handler as Handler);
    return () => set!.delete(handler as Handler);
  }

  /** Subscribe to every event. Useful for audio and debug overlays. */
  onAny(handler: Handler): () => void {
    this.#any.add(handler);
    return () => this.#any.delete(handler);
  }

  emit(event: GameEvent): void {
    const set = this.#handlers.get(event.type);
    if (set) for (const h of set) h(event);
    for (const h of this.#any) h(event);
  }

  clear(): void {
    this.#handlers.clear();
    this.#any.clear();
  }
}

/** Shared app-wide bus instance. */
export const bus = new EventBus();
